import { useRouterState } from "@tanstack/react-router";
import { ChevronDown, ChevronUp } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { ListContainer, ListItem } from "@/components/common/ListItem";
import { UnreadBadgeStack } from "@/components/common/UnreadBadgeStack";
import { Button } from "@/components/ui/button";
import { useMyStudyGroupsPaginated } from "@/hooks/useMyStudyGroupsPaginated";
import { useStudyGroupNavigation } from "@/hooks/useStudyGroupNavigation";
import { cn } from "@/lib/utils";

interface StudyGroupListProps {
  containerHeight?: string | number;
  containerClassName?: string;
  isExpanded?: boolean;
  onHeaderToggle?: () => void;
}

const buildUnreadBadgeItems = (
  t: (key: string) => string,
  unreadPostCommentCount: number,
  unreadMentions: number,
) => [
  {
    count: unreadPostCommentCount,
    label: t("studyGroup.unreadPostsAndComments"),
    variant: "unreadActivity" as const,
  },
  {
    count: unreadMentions,
    label: t("studyGroup.unreadMentions"),
    variant: "mention" as const,
    prefix: "@",
  },
];

export const StudyGroupList: React.FC<StudyGroupListProps> = ({
  containerHeight = "450px",
  containerClassName,
  isExpanded = true,
  onHeaderToggle,
}) => {
  const { navigateToStudyGroup } = useStudyGroupNavigation();
  const { t } = useTranslation();
  const routerMatches = useRouterState({
    select: (state) => state.matches,
  });
  const activeStudyGroupId = routerMatches
    .map(
      (m) =>
        (m.params as Record<string, string | undefined>)?.["study-group-id"],
    )
    .find(Boolean);

  const {
    accumulatedGroups,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    totalUnreadCounts,
  } = useMyStudyGroupsPaginated(50);

  const handleGroupClick = (studyGroupId: number) => {
    navigateToStudyGroup(studyGroupId);
  };

  const renderCountBadges = (group: (typeof accumulatedGroups)[number]) => {
    const unreadPostAndCommentCount = group.unread_post_comment_count ?? 0;
    const unreadMentions = group.unread_mention_count ?? 0;

    if (!unreadPostAndCommentCount && !unreadMentions) {
      return null;
    }

    return (
      <UnreadBadgeStack
        items={buildUnreadBadgeItems(
          t,
          unreadPostAndCommentCount,
          unreadMentions,
        )}
        align="end"
        className="pt-1"
      />
    );
  };

  const renderContent = () => {
    if (!isExpanded) {
      return null;
    }

    if (isLoading) {
      return (
        <div className="py-4 text-center text-text-subtitle">
          {t("common.loading")}
        </div>
      );
    }

    if (error) {
      return <div className="py-4 text-center text-red-500">{error}</div>;
    }

    if (!accumulatedGroups || accumulatedGroups.length === 0) {
      return (
        <div className="py-4 text-center text-text-subtitle">
          {t("common.noStudyGroupsFound")}
        </div>
      );
    }

    return (
      <>
        {accumulatedGroups.map((group) => (
          <ListItem
            key={group.id}
            icon={
              <div className="relative">
                <CustomAvatar
                  name={group.name}
                  imageUrl={group.image_url}
                  size={32}
                />
              </div>
            }
            label={group.name}
            onClick={() => handleGroupClick(group.id)}
            selected={activeStudyGroupId === String(group.id)}
            rightContent={renderCountBadges(group)}
          />
        ))}

        {hasMore && (
          <div className="p-2 text-center">
            <Button variant="teal" onClick={loadMore} disabled={isLoadingMore}>
              {t("common.showMore")}
            </Button>
          </div>
        )}
      </>
    );
  };

  const headerRowClassName = cn(
    "flex h-[34px] w-full items-center justify-between px-1.5 text-left",
    isExpanded
      ? "-mt-[3px]"
      : "-ml-1.5 h-[34px] min-h-[34px] w-[calc(var(--sidebar-width)-var(--sidebar-padding-left)-var(--sidebar-padding-right))] rounded-[0px_30px_30px_0px] bg-custom-white pl-4 pr-1.5 shadow-[0px_4px_15px_rgba(0,0,0,0.1)]",
  );
  const headerRowContent = (
    <>
      <h2 className="inline-flex items-center gap-2 text-xl font-semibold text-text-subtitle">
        <span>{t("common.studyGroups")}</span>
        <UnreadBadgeStack
          items={buildUnreadBadgeItems(
            t,
            totalUnreadCounts.unreadPostCommentCount,
            totalUnreadCounts.unreadMentions,
          )}
          align="start"
          display="inline-flex"
        />
      </h2>
      {onHeaderToggle ? (
        <span className="inline-flex size-6 items-center justify-center rounded-lg p-0.5">
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </span>
      ) : null}
    </>
  );
  const header = onHeaderToggle ? (
    <button
      type="button"
      onClick={onHeaderToggle}
      className={cn(headerRowClassName, "cursor-pointer border-0")}
      aria-label={isExpanded ? "Collapse section" : "Expand section"}
      aria-expanded={isExpanded}
    >
      {headerRowContent}
    </button>
  ) : (
    <div className={headerRowClassName}>{headerRowContent}</div>
  );

  return (
    <ListContainer
      header={header}
      containerHeight={containerHeight}
      className={cn(
        isExpanded
          ? "overflow-x-hidden"
          : "overflow-x-visible overflow-y-visible",
        containerClassName,
        !isExpanded && "rounded-none bg-transparent p-0",
      )}
      dividerClassName={cn("", !isExpanded && "hidden")}
      scrollClassName={
        isExpanded ? "overflow-y-auto [scrollbar-gutter:stable]" : "hidden"
      }
    >
      {renderContent()}
    </ListContainer>
  );
};
